import React, {
  useState,
  useCallback,
  ReactElement,
  cloneElement,
} from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  FormHelperText,
} from '@mui/material';
import { Button, RowLayout } from '@/components/atoms';
import { Card } from '@/components/molecules';
import { FormProps } from './FormContainer.props';

/**
 * Enhanced Form Container - MUI FormControl Integration with Translation Support
 *
 * ✅ **KEY FEATURES**:
 * - **Translation-Ready**: Only displays error messages when explicitly provided in rules.messages
 * - **No Default Messages**: Respects translation systems - no fallback messages
 * - **MUI FormControl Integration**: Automatic wrapping for proper accessibility
 * - **Validation Timing Control**: Choose when errors appear (change/submit/blur)
 * - **Progressive Enhancement**: Fields work normally without validation rules
 *
 * 🌐 **Translation Support**:
 * - **Explicit Messages Only**: Error messages only shown when provided in validationRules.messages
 * - **No Hardcoded Text**: Allows complete control over error messaging for i18n
 * - **Empty Validation**: Validation still works, but no error display without messages
 *
 * @example
 * ```tsx
 * // Translation-ready validation
 * <Form
 *   validationRules={{
 *     name: {
 *       required: true,
 *       messages: {
 *         required: t('validation.name.required') // Your translation function
 *       }
 *     },
 *     email: {
 *       required: true,
 *       email: true,
 *       messages: {
 *         required: t('validation.email.required'),
 *         email: t('validation.email.invalid')
 *       }
 *     }
 *   }}
 * >
 *   <TextField id="name" label="Name" />
 *   <TextField id="email" label="Email" />
 * </Form>
 * ```
 */ export const Form: React.FC<FormProps> = ({
  title,
  subtitle,
  layout = 'plain',
  initialValues = {},
  onSubmit,
  onChange,
  onValidationChange,
  onReset,
  validationRules = {},
  fieldErrors = {},
  validateOn = 'change',
  showFieldErrors = true,
  hideErrorsForFields = [],
  children,
  showActions = false,
  submitText = 'Submit',
  resetText = 'Reset',
  disabled = false,
  containerProps = {},
  sx,
  className,
  'data-testid': testId,
}) => {
  // State management
  const [formValues, setFormValues] =
    useState<Record<string, any>>(initialValues);
  const [fieldValidationErrors, setFieldValidationErrors] = useState<
    Record<string, string>
  >({});
  const [showValidationErrors, setShowValidationErrors] = useState(
    validateOn === 'change'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation handlers organized by type for better maintainability
  const validationHandlers = {
    required: (value: any, rules: any) =>
      rules.required &&
      (!value || (typeof value === 'string' && value.trim() === ''))
        ? rules.messages?.required || ''
        : null,

    minLength: (value: string, rules: any) =>
      rules.minLength && value.length < rules.minLength
        ? rules.messages?.minLength || ''
        : null,

    maxLength: (value: string, rules: any) =>
      rules.maxLength && value.length > rules.maxLength
        ? rules.messages?.maxLength || ''
        : null,

    pattern: (value: string, rules: any) =>
      rules.pattern && !rules.pattern.test(value)
        ? rules.messages?.pattern || ''
        : null,

    email: (value: string, rules: any) =>
      rules.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        ? rules.messages?.email || ''
        : null,

    number: (value: any, rules: any) => {
      if (!rules.number) return null;
      const numValue = Number(value);
      if (isNaN(numValue)) return rules.messages?.number || '';
      if (rules.min !== undefined && numValue < rules.min)
        return rules.messages?.min || '';
      if (rules.max !== undefined && numValue > rules.max)
        return rules.messages?.max || '';
      return null;
    },

    custom: (value: any, rules: any) =>
      rules.custom ? rules.custom(value) : null,
  };

  // Validate a single field using validation handlers
  const validateField = useCallback(
    (fieldId: string, value: any, rules: any): string => {
      // Check required first
      const requiredError = validationHandlers.required(value, rules);
      if (requiredError) return requiredError;

      // Skip other validations if value is empty
      if (!value || (typeof value === 'string' && value.trim() === ''))
        return '';

      const stringValue = String(value);

      // Run validation handlers in order, return first error found
      const validators = [
        'minLength',
        'maxLength',
        'pattern',
        'email',
        'number',
        'custom',
      ];

      for (const validatorName of validators) {
        const handler =
          validationHandlers[validatorName as keyof typeof validationHandlers];
        const error = handler(
          validatorName === 'number' ? value : stringValue,
          rules
        );
        if (error) return error;
      }

      return '';
    },
    []
  );

  // Handle field value changes with validation
  const handleFieldChange = useCallback(
    (fieldId: string, value: any) => {
      setFormValues((prev) => {
        const newValues = { ...prev, [fieldId]: value };

        // Validate field if rules exist
        const rules = validationRules[fieldId];
        const fieldError = rules ? validateField(fieldId, value, rules) : '';

        // Update field errors (always track them, but only show when appropriate)
        setFieldValidationErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          if (fieldError) {
            newErrors[fieldId] = fieldError;
          } else {
            delete newErrors[fieldId];
          }

          // Combine validation errors with external errors
          const allErrors = { ...newErrors, ...fieldErrors };
          const isValid = Object.keys(allErrors).length === 0;

          // Call callbacks
          onChange?.(newValues, allErrors);
          onValidationChange?.(allErrors, isValid);

          return newErrors;
        });

        return newValues;
      });
    },
    [validationRules, fieldErrors, onChange, onValidationChange]
  );

  // Validate all fields for submission
  const validateAllFields = useCallback(() => {
    const errors: Record<string, string> = {};

    Object.keys(validationRules).forEach((fieldId) => {
      const value = formValues[fieldId];
      const rules = validationRules[fieldId];
      const fieldError = validateField(fieldId, value, rules);

      if (fieldError) {
        errors[fieldId] = fieldError;
      }
    });

    return errors;
  }, [formValues, validationRules, validateField]);

  // Enhance children with MUI FormControl wrapping and validation
  const enhanceChildren = useCallback(
    (children: React.ReactNode): React.ReactNode => {
      return React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;

        const element = child as ReactElement<any>;
        const props = element.props || {};

        // Get field ID from props
        const fieldId =
          props.id || props['data-field-id'] || props.name || props.fieldId;

        // Recursively enhance nested children
        const enhancedChildren = props.children
          ? enhanceChildren(props.children)
          : props.children;

        // If no field ID, just return with enhanced children
        if (!fieldId) {
          return enhancedChildren !== props.children
            ? cloneElement(element, { ...props, children: enhancedChildren })
            : element;
        }

        // Get current field state
        const currentValue =
          formValues[fieldId] ?? props.value ?? props.defaultValue ?? '';
        const fieldError =
          (showValidationErrors ? fieldValidationErrors[fieldId] : null) ||
          fieldErrors[fieldId];
        const hasError = Boolean(fieldError);
        const isRequired = validationRules[fieldId]?.required;

        // Check if we should show error message for this field
        const shouldShowErrorMessage = () => {
          // Check if field is in hideErrorsForFields array
          if (hideErrorsForFields?.includes(fieldId)) {
            return false;
          }

          // Fall back to form-level showFieldErrors setting
          return showFieldErrors !== false;
        };

        const showError = shouldShowErrorMessage();

        // Enhanced props with MUI validation state (avoid duplicate error display)
        const enhancedProps = {
          ...props,
          value: currentValue,
          error: hasError,
          // For fields with validation rules, don't pass helperText (FormControl handles it)
          // For fields without validation rules, pass helperText normally
          helperText: validationRules[fieldId]
            ? props.helperText
            : showError
              ? fieldError || props.helperText
              : props.helperText,
          disabled: disabled || props.disabled,
          required: isRequired || props.required,
          onChange: (eventOrValue: any, ...args: any[]) => {
            // Extract value from different input patterns
            let newValue: any;
            if (eventOrValue?.target) {
              const target = eventOrValue.target;
              newValue =
                target.type === 'checkbox' ? target.checked : target.value;
            } else if (Array.isArray(eventOrValue)) {
              newValue = eventOrValue;
            } else {
              newValue = eventOrValue;
            }

            // Call original onChange to preserve component behavior
            if (props.onChange) {
              props.onChange(eventOrValue, ...args);
            }

            // Update form state with validation
            handleFieldChange(fieldId, newValue);
          },
          ...(enhancedChildren !== props.children && {
            children: enhancedChildren,
          }),
        };

        // For fields with validation rules, wrap in MUI FormControl
        if (validationRules[fieldId]) {
          return (
            <FormControl
              key={fieldId}
              error={hasError}
              required={isRequired}
              disabled={disabled || props.disabled}
              fullWidth
              margin="normal"
              sx={{ mb: 2 }}
            >
              {cloneElement(element, enhancedProps)}
              {hasError && showError && (
                <FormHelperText error>{fieldError}</FormHelperText>
              )}
            </FormControl>
          );
        }

        // Return enhanced element without FormControl wrapper for non-validated fields
        return cloneElement(element, enhancedProps);
      });
    },
    [
      formValues,
      disabled,
      handleFieldChange,
      fieldValidationErrors,
      fieldErrors,
      validationRules,
      showValidationErrors,
    ]
  );

  // Handle form submission with MUI validation
  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      setIsSubmitting(true);

      // Show validation errors on submit
      setShowValidationErrors(true);

      // Validate all fields before submission
      const validationErrors = validateAllFields();
      const allErrors = { ...validationErrors, ...fieldErrors };

      if (Object.keys(allErrors).length > 0) {
        // Update validation errors to show all field errors
        setFieldValidationErrors(validationErrors);
        onValidationChange?.(allErrors, false);
        setIsSubmitting(false);
        return;
      }

      // Submit form if validation passes
      onSubmit?.(formValues);
      setIsSubmitting(false);
    },
    [formValues, onSubmit, validateAllFields, fieldErrors, onValidationChange]
  );

  // Handle form reset
  const handleReset = useCallback(() => {
    setFormValues(initialValues);
    setFieldValidationErrors({});
    onReset?.();
  }, [initialValues, onReset]);

  // Render form content
  const formContent = (
    <Box component="form" onSubmit={handleSubmit}>
      {/* Header */}
      {(title || subtitle) && (
        <Box sx={{ mb: 2 }}>
          {title && (
            <Typography variant="h5" component="h2" gutterBottom>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      {/* Form content - any children (fields, images, boxes, etc.) */}
      {enhanceChildren(children)}

      {/* Form actions */}
      {showActions && (
        <Box sx={{ mt: 3 }}>
          <RowLayout spacing="sm" justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={disabled || isSubmitting}
            >
              {resetText}
            </Button>
            <Button
              variant="filled"
              color="primary"
              onClick={handleSubmit}
              disabled={disabled || isSubmitting}
              loading={isSubmitting}
            >
              {submitText}
            </Button>
          </RowLayout>
        </Box>
      )}
    </Box>
  );

  // Render with layout
  const containerSx = {
    ...(layout === 'paper' && { p: 3 }),
    ...(layout === 'card' && { p: 2 }),
    ...sx,
    // Ensure white background - placed after sx to prevent override unless explicitly set in sx
    ...((!sx || (!('bgcolor' in sx) && !('backgroundColor' in sx))) && {
      bgcolor: 'background.paper',
    }),
  };

  if (layout === 'card') {
    return (
      <Card
        {...containerProps}
        sx={{
          ...containerSx,
          '& .MuiCardContent-root': {
            padding: 3,
          },
        }}
        className={className}
        data-testid={testId}
        content={formContent}
      />
    );
  }

  if (layout === 'paper') {
    return (
      <Paper
        {...containerProps}
        sx={containerSx}
        className={className}
        data-testid={testId}
      >
        {formContent}
      </Paper>
    );
  }

  // Plain layout
  return (
    <Box sx={sx} className={className} data-testid={testId}>
      {formContent}
    </Box>
  );
};

export default Form;
